import { useCallback, useMemo, useState } from "react";

import AlertComponent from "../../../helpers/alert/AlertComponent";
import { triggerDownload } from "../lib/fileDownload";
import { isSinGestion, isSinTipoGestion, normalizeLabel } from "../lib/labels";
import {
  MANAGEMENT_VARIANT,
  SIN_GESTION_ALERTA_ID,
  SIN_TIPO_GESTION_ID,
} from "./constants";
import { buildManagementMetaItems } from "./buildManagementMetaItems";
import { getAlertedProductsManagementColumns } from "./getAlertedProductsManagementColumns";
import { buildAddAlertColumns } from "./columns/buildAddAlertColumns";
import { buildHomologationColumn } from "./columns/buildHomologationColumn";
import { buildPriceColumn } from "./columns/buildPriceColumn";
import { useAddAlertModal } from "./useAddAlertModal";
import { useActaFile } from "./useActaFile";
import { useHomologation } from "./useHomologation";
import { useManagementSubmit } from "./useManagementSubmit";
import { usePdfViewer } from "./usePdfViewer";
import { usePriceAdjustment } from "./usePriceAdjustment";

// Orquesta la gestión estándar (todas las variantes salvo INDETERMINADO):
// compone los hooks de estado, deriva columnas/metadatos y expone la API que la
// UI del widget consume. Mantiene el componente de presentación libre de lógica.
export const useStandardManagement = ({
  assignment,
  appliedFilters,
  historyByCategory = { pdf: [], excel: [] },
  managementTypeOptions = [],
  variant = MANAGEMENT_VARIANT.DEFAULT,
  onContinue,
  onSubmitManagementRequest,
}) => {
  const isPriceAdjustment = variant === MANAGEMENT_VARIANT.PRICE_ADJUSTMENT;
  const isHomologation = variant === MANAGEMENT_VARIANT.HOMOLOGATION;

  const [observation, setObservation] = useState("");
  const [alertsData, setAlertsData] = useState(() => assignment?.selectedRows ?? []);
  const [addingId, setAddingId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [missing, setMissing] = useState({ isOpen: false, message: "" });
  const [servicesResult, setServicesResult] = useState({ isOpen: false, result: null });

  const pdf = usePdfViewer();
  const acta = useActaFile({
    isPriceAdjustment,
    managementTypeLabel: assignment?.managementType,
  });
  const priceAdjustment = usePriceAdjustment(isPriceAdjustment);
  const homologation = useHomologation(isHomologation);

  const activePdf = historyByCategory.pdf?.[0] ?? null;
  const activeExcel = historyByCategory.excel?.[0] ?? null;
  const assignmentManagementTypeCode = assignment?.selectedRows?.[0]?.managementTypeCode;

  const managementTypeOption = useMemo(
    () =>
      managementTypeOptions.find(
        (opt) =>
          String(opt.value) === String(assignmentManagementTypeCode) ||
          normalizeLabel(opt.label) === normalizeLabel(assignment?.managementType)
      ) ||
      (assignment?.managementType
        ? { value: assignmentManagementTypeCode ?? null, label: assignment.managementType }
        : null),
    [assignment?.managementType, assignmentManagementTypeCode, managementTypeOptions]
  );

  const managementMetaItems = useMemo(
    () =>
      buildManagementMetaItems(managementTypeOption?.label || assignment?.managementType || "", {
        isPriceAdjustment,
        isHomologation,
      }),
    [assignment?.managementType, managementTypeOption?.label, isPriceAdjustment, isHomologation]
  );

  // Homologación: la paginación del servicio ya restringe a productos sin tipo
  // de gestión y con gestión alerta "Sin Gestión".
  const modalExtraFilters = useMemo(
    () =>
      isHomologation
        ? {
            managementType: { value: SIN_TIPO_GESTION_ID },
            alertManagement: { value: SIN_GESTION_ALERTA_ID },
          }
        : null,
    [isHomologation]
  );

  const { allRows: modalAllRows, loading: modalLoading } = useAddAlertModal({
    isOpen: isAddModalOpen || homologation.modal.isOpen,
    appliedFilters,
    extraFilters: modalExtraFilters,
  });

  const addedIds = useMemo(
    () => new Set(alertsData.map((r) => r.orderDetailId).filter(Boolean)),
    [alertsData]
  );
  const managementCategoryCodes = useMemo(
    () => new Set(alertsData.map((r) => r.alertCategoryCode).filter(Boolean)),
    [alertsData]
  );

  const modalDataSource = useMemo(
    () =>
      modalAllRows.filter((row) => {
        if (managementCategoryCodes.size > 0 && !managementCategoryCodes.has(row.alertCategoryCode))
          return false;
        if (row.orderDetailId && addedIds.has(row.orderDetailId)) return false;
        if (!isSinGestion(row.alertManagement ?? "")) return false;
        // Homologación: además de "Sin Gestión", solo productos sin tipo de
        // gestión asignada.
        if (isHomologation && !isSinTipoGestion(row.managementType ?? "")) return false;
        return true;
      }),
    [modalAllRows, addedIds, managementCategoryCodes, isHomologation]
  );

  const handleRemoveAlert = useCallback(
    (record) => {
      setAlertsData((prev) => prev.filter((row) => row.id !== record.id));
      priceAdjustment.clearRow(record.id);
      homologation.clearRow(record.id);
    },
    [priceAdjustment, homologation]
  );

  const handleAddAlert = useCallback(
    (product) => {
      if (!managementTypeOption) {
        AlertComponent.warning(
          "Tipo de gestión requerido",
          "No fue posible identificar el tipo de gestión de la solicitud actual."
        );
        return;
      }
      setAddingId(product.id);
      setAlertsData((prev) => [
        ...prev,
        {
          ...product,
          managementType: managementTypeOption.label,
          managementTypeCode: managementTypeOption.value,
          hasAssignedManagementType: true,
        },
      ]);
      setAddingId(null);
    },
    [managementTypeOption]
  );

  const alertsColumns = useMemo(
    () =>
      getAlertedProductsManagementColumns({
        onRemove: handleRemoveAlert,
        priceColumn: isPriceAdjustment
          ? buildPriceColumn({
              prices: priceAdjustment.newSalePrices,
              touched: priceAdjustment.touched,
              onChange: priceAdjustment.setPrice,
            })
          : null,
        homologationColumn: isHomologation
          ? buildHomologationColumn({
              byRow: homologation.byRow,
              touched: homologation.touched,
              onAssign: homologation.openModal,
              onRemove: homologation.removeRow,
            })
          : null,
      }),
    [
      handleRemoveAlert,
      isPriceAdjustment,
      isHomologation,
      priceAdjustment.newSalePrices,
      priceAdjustment.touched,
      priceAdjustment.setPrice,
      homologation.byRow,
      homologation.touched,
      homologation.openModal,
      homologation.removeRow,
    ]
  );

  const addAlertColumns = useMemo(
    () =>
      buildAddAlertColumns({
        addedIds,
        addingId,
        onAdd: handleAddAlert,
        managementTypeLabel: managementTypeOption?.label,
      }),
    [addedIds, addingId, handleAddAlert, managementTypeOption?.label]
  );

  const handleDownloadActa = useCallback(() => {
    if (!acta.actaFile) return;
    const url = URL.createObjectURL(acta.actaFile);
    triggerDownload(url, acta.actaFile.name);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [acta.actaFile]);

  const closeServicesResult = useCallback(() => {
    const shouldContinue = Boolean(servicesResult.result?.success);
    setServicesResult({ isOpen: false, result: null });
    if (shouldContinue) onContinue?.();
  }, [servicesResult.result?.success, onContinue]);

  const { submitting, submit } = useManagementSubmit({
    observation,
    actaFile: acta.actaFile,
    alertsData,
    managementTypeOption,
    priceAdjustment,
    homologation,
    onSubmitManagementRequest,
    onContinue,
    onMissing: (message) => setMissing({ isOpen: true, message }),
    onResult: (result) => {
      // Éxito de ambos servicios: modal genérico de la app con el mensaje del
      // segundo servicio (solicitud) y el id_encabezado del primero (gestión).
      if (result?.success) {
        const mensaje =
          result?.request?.message || "Solicitud creada correctamente.";
        AlertComponent.success(
          "Servicios procesados correctamente",
          `${mensaje} El id de la solicitud es: ${result?.encabezadoId ?? "—"}`
        ).then(() => onContinue?.());
        return;
      }
      // Si algún servicio falla, se muestran los errores como hasta ahora.
      setServicesResult({ isOpen: true, result });
    },
  });

  return {
    isPriceAdjustment,
    isHomologation,
    managementTypeLabel: managementTypeOption?.label || assignment?.managementType || "",
    observation,
    setObservation,
    alertsData,
    alertsColumns,
    addAlertColumns,
    modalDataSource,
    modalLoading,
    isAddModalOpen,
    setIsAddModalOpen,
    missing,
    setMissing,
    servicesResult,
    closeServicesResult,
    managementMetaItems,
    activePdf,
    activeExcel,
    pdf,
    acta,
    homologation,
    handleDownloadActa,
    submitting,
    submit,
  };
};